import React, { useContext, useState } from 'react';
import { AdminContext } from 'Store/Admin';
import { validateRulesFieldValue } from 'components/util/validation';
import { Stack, TextField, TooltipHost, getTheme } from '@fluentui/react';
import { settingsFieldStyles } from '../SettingsStyles';

interface TimeFieldProps {
  config: TimeConfig;
  label: { days?: string; hours: string; minutes: string };
  value: number;
  tooltip: string;
  className: string;
}

export const enum TimeConfig {
  CaseOpenLifetimeInSeconds,
  CaseAssignedLifetimeInSeconds,
  CaseOpenLifetimeMaxInSeconds
}

const SECONDS_PER_DAY = 86400;
const SECONDS_PER_HOUR = 3600;
const SECONDS_PER_MINUTE = 60;

export const TimeField: React.FC<TimeFieldProps> = props => {
  const { palette, fonts } = getTheme();
  const { dispatch } = useContext(AdminContext);

  const initialDaysValue =
    props.config === TimeConfig.CaseOpenLifetimeMaxInSeconds ? Math.floor(props.value / SECONDS_PER_DAY) : 0;
  const [days, setDays] = useState<number>(initialDaysValue);

  const initialHoursValue =
    props.config === TimeConfig.CaseOpenLifetimeMaxInSeconds
      ? Math.floor((props.value - days * SECONDS_PER_DAY) / SECONDS_PER_HOUR)
      : Math.floor(props.value / SECONDS_PER_HOUR);
  const [hours, setHours] = useState<number>(initialHoursValue);

  const initialMinutesValue =
    props.config === TimeConfig.CaseOpenLifetimeMaxInSeconds
      ? Math.floor((props.value - (days * SECONDS_PER_DAY + hours * SECONDS_PER_HOUR)) / SECONDS_PER_MINUTE)
      : Math.floor((props.value - hours * SECONDS_PER_HOUR) / SECONDS_PER_MINUTE);
  const [minutes, setMinutes] = useState<number>(initialMinutesValue);

  let DD: number;
  let HH: number;
  let MM: number;

  const onComponentChange = (newTimeInputValue: string | undefined, fieldName: string): void => {
    if (newTimeInputValue === undefined) {
      return;
    }
    let result: number = parseInt(newTimeInputValue);
    let correctResult: number;
    result = isNaN(result) ? 0 : result;

    switch (fieldName) {
      case 'days':
        correctResult = Number(validateRulesFieldValue(String(result), 0, 41));
        setDays(correctResult);
        DD = correctResult;
        break;
      case 'hours': {
        const maxAllowedHoursValue = props.config === TimeConfig.CaseOpenLifetimeMaxInSeconds ? 23 : 1000;
        correctResult = Number(validateRulesFieldValue(String(result), 0, maxAllowedHoursValue));
        setHours(correctResult);
        HH = correctResult;
        break;
      }
      case 'minutes':
        correctResult = Number(validateRulesFieldValue(String(result), 0, 59));
        setMinutes(correctResult);
        MM = correctResult;
        break;
    }
    const timeInSeconds =
      (DD != undefined ? DD : days) * SECONDS_PER_DAY +
      (HH != undefined ? HH : hours) * SECONDS_PER_HOUR +
      (MM != undefined ? MM : minutes) * SECONDS_PER_MINUTE;

    if (props.config === TimeConfig.CaseOpenLifetimeInSeconds) {
      dispatch({
        type: 'CASE_OPEN_LIFETIME_IN_SECONDS',
        payload: timeInSeconds
      });
    } else if (props.config === TimeConfig.CaseAssignedLifetimeInSeconds) {
      dispatch({
        type: 'CASE_ASSIGNED_LIFETIME_IN_SECONDS',
        payload: timeInSeconds
      });
    } else if (props.config === TimeConfig.CaseOpenLifetimeMaxInSeconds) {
      dispatch({
        type: 'CASE_OPEN_LIFETIME_MAX_IN_SECONDS',
        payload: timeInSeconds
      });
    }
    dispatch({
      type: 'IS_UPDATED',
      payload: true
    });
  };

  return (
    <div className={props.className}>
      <TooltipHost content={props.tooltip}>
        <Stack horizontal tokens={{ childrenGap: 15 }}>
          {props.config === TimeConfig.CaseOpenLifetimeMaxInSeconds && (
            <Stack.Item>
              <TextField
                onChange={(_, newDaysValue): void => onComponentChange(newDaysValue, 'days')}
                label={props.label.days}
                placeholder={days.toString()}
                value={days.toString()}
                styles={settingsFieldStyles(palette, fonts, 60)}
                maxLength={2}
              />
            </Stack.Item>
          )}
          <Stack.Item>
            <TextField
              onChange={(_, newHoursValue): void => onComponentChange(newHoursValue, 'hours')}
              label={props.label.hours}
              placeholder={hours.toString()}
              value={hours.toString()}
              styles={settingsFieldStyles(palette, fonts, 60)}
              maxLength={props.config === TimeConfig.CaseOpenLifetimeMaxInSeconds ? 2 : 4}
            />
          </Stack.Item>
          <Stack.Item>
            <TextField
              onChange={(_, newMinutesValue): void => onComponentChange(newMinutesValue, 'minutes')}
              label={props.label.minutes}
              placeholder={minutes.toString()}
              value={minutes.toString()}
              styles={settingsFieldStyles(palette, fonts, 60)}
              maxLength={2}
            />
          </Stack.Item>
        </Stack>
      </TooltipHost>
    </div>
  );
};
